import React, { useCallback, useEffect, useState } from 'react';

import type { ContextVariables, DynamicCardStepComponent } from 'api/types';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import MessageContainer from 'components/MessageContainer';
import Box from '@mui/material/Box';
import { Typography } from '@forethought-technologies/forethought-elements';
import { useTheme } from '@mui/material/styles';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPhotoCancel,
} from '@tabler/icons-react';
import { useUpdateConversation } from 'components/App/hooks';
import { useAppDispatch, useAppSelector } from 'hooks';
import useEmblaCarousel from 'embla-carousel-react';
import { selectIsAuthorIconVisible } from 'reducers/workflows';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { ORG_THEMED_TEXT_BUTTON_SX } from 'components/ORG_THEMED_TEXT_BUTTON_SX';

type EmblaApi = ReturnType<typeof useEmblaCarousel>[1];

const MAX_IMAGE_COMPONENT_IMAGES = 3;
const IMAGE_HEIGHT = '160px';
const SLIDE_SPACING = 8;
const SLIDE_PERCENT_OF_CONTAINER = '90%';

const OPTIONS = {
  containScroll: false,
  dragFree: true,
  duration: 20,
} as const;

const usePrevNextButtons = (emblaApi: EmblaApi) => {
  // https://codesandbox.io/p/sandbox/embla-carousel-default-react-5y436f?file=%2Fsrc%2Fjs%2FEmblaCarouselDotButton.jsx
  // documented in https://www.embla-carousel.com/examples/predefined/
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaApi) => {
    setPrevBtnDisabled(!emblaApi?.canScrollPrev());
    setNextBtnDisabled(!emblaApi?.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
  };
};

interface DynamicCardStepProps {
  isLastStep: boolean;
  setStepContextVariables: (contextVariables: ContextVariables) => {
    payload: {
      contextVariables: ContextVariables;
      index: number;
    };
    type: string;
  };
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: DynamicCardStepComponent;
}

export const DynamicCardStep = ({
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}: DynamicCardStepProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);

  const updateConversation = useUpdateConversation();
  const dispatch = useAppDispatch();
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  const { cards, content_config, forethought_text, message, output_variable } =
    widgetComponent.component_fields;
  const { isReroutedByNegativeFeedback, isSent } = widgetComponent;

  const onSubmit = (e: React.SyntheticEvent, index: number) => {
    e.preventDefault();

    const card = cards[index];

    if (!card || isSent || isReroutedByNegativeFeedback) {
      return;
    }

    const stepContextVariables = {
      [output_variable]: card.value,
    };

    dispatch(setStepContextVariables(stepContextVariables));

    setStepSent();
    updateConversation(stepContextVariables);
  };

  return (
    <MessageContainer
      className={`has-link ${isSent ? 'js-sent-dynamic-card-message' : ''}`}
    >
      {message && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {message}
        </AgathaMessage>
      )}
      <Box ref={emblaRef}>
        <Box
          sx={[
            {
              backfaceVisibility: 'hidden',
              display: 'flex',
              marginLeft: `calc(${SLIDE_SPACING}px * -1)`,
              mb: 2,
              touchAction: 'pan-y',
            },
            !message && {
              mt: 2,
            },
          ]}
        >
          {cards.map((card, index) => (
            <Card
              card={card}
              contentConfig={content_config}
              index={index}
              isSent={isSent || isReroutedByNegativeFeedback}
              key={index}
              numberOfCards={cards.length}
              onSubmit={onSubmit}
              text={forethought_text}
            />
          ))}
        </Box>
      </Box>
      {!isSent && !isReroutedByNegativeFeedback && (
        <CarouselControl emblaApi={emblaApi} text={forethought_text} />
      )}
    </MessageContainer>
  );
};

interface CardProps {
  card?: DynamicCardStepComponent['component_fields']['cards'][number];
  contentConfig: DynamicCardStepComponent['component_fields']['content_config'];
  index: number;
  isSent?: boolean;
  numberOfCards: number;
  onSubmit: (e: React.SyntheticEvent, index: number) => void;
  text: DynamicCardStepComponent['component_fields']['forethought_text'];
}

const Card = ({
  card,
  contentConfig,
  index,
  isSent = false,
  numberOfCards,
  onSubmit,
  text,
}: CardProps) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        flex: `0 0 ${SLIDE_PERCENT_OF_CONTAINER}`,
        minWidth: 0,
        paddingLeft: `${SLIDE_SPACING}px`,
      }}
    >
      <Box
        borderRadius='8px'
        className={index === 0 ? 'js-first-dynamic-card' : ''}
        component='button'
        dir='auto'
        disabled={isSent}
        display='flex'
        flexDirection='column'
        gap={1}
        onClick={e => onSubmit(e, index)}
        p={2}
        sx={[
          {
            backgroundColor: 'white',
            border: `1px solid ${palette.colors.slate[300]}`,
            boxShadow: '1px 0px 8px 0px rgba(0, 0, 0, 0.08)',
            textAlign: 'left',
            transition: 'all 0.2s ease-in-out',
            width: '100%',
          },
          !isSent && {
            '&:hover': {
              backgroundColor: palette.colors.slate[100],
            },
            cursor: 'pointer',
          },
        ]}
      >
        <Typography color={palette.colors.grey[600]} variant='font14'>
          {index + 1} of {numberOfCards}
        </Typography>
        {card &&
          card.displayable_dynamic_sections.map((section, index) => (
            <Section
              contentConfig={contentConfig}
              key={index}
              section={section}
              text={text}
            />
          ))}
      </Box>
    </Box>
  );
};

interface SectionProps {
  contentConfig: DynamicCardStepComponent['component_fields']['content_config'];
  section: DynamicCardStepComponent['component_fields']['cards'][number]['displayable_dynamic_sections'][number];
  text: DynamicCardStepComponent['component_fields']['forethought_text'];
}

const Section = ({ contentConfig, section, text }: SectionProps) => {
  const { palette } = useTheme();
  const [isViewMore, setIsViewMore] = useState(false);

  switch (section.type) {
    case 'content':
      return (
        <Box display='flex' flexDirection='column' gap={0.5}>
          {section.components.map((component, index) => (
            <React.Fragment key={index}>
              {contentConfig &&
              index >= contentConfig.limit &&
              !isViewMore ? null : (
                <Box dir='auto'>
                  {component.label && (
                    <Typography
                      color={palette.colors.grey[600]}
                      variant='font14'
                    >
                      {component.label}
                    </Typography>
                  )}
                  <Typography color={palette.colors.black} variant='font14'>
                    {' '}
                    {component.value}
                  </Typography>
                </Box>
              )}
            </React.Fragment>
          ))}
          {contentConfig && (
            <OrgThemedButton
              onClick={e => {
                e.stopPropagation();
                setIsViewMore(prev => !prev);
              }}
              size='medium'
              sx={ORG_THEMED_TEXT_BUTTON_SX}
              variant='text'
            >
              {isViewMore ? text.view_less : text.view_more}
            </OrgThemedButton>
          )}
        </Box>
      );
    case 'image':
      return section.components.length ? (
        <Box display='flex' gap={1} width='100%'>
          {section.components
            .slice(0, MAX_IMAGE_COMPONENT_IMAGES)
            .map((section, key) => (
              <Box
                key={key}
                sx={{
                  backgroundColor: palette.colors.white,
                  border: `1px solid ${palette.colors.slate[200]}`,
                  borderRadius: '8px',
                  height: IMAGE_HEIGHT,
                  img: { height: '100%', objectFit: 'contain', width: '100%' },
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <img alt='' src={section.value} />
              </Box>
            ))}
        </Box>
      ) : (
        <EmptyImage />
      );
    case 'title':
      return (
        <Box display='flex' flexDirection='column' gap={0.5}>
          {section.components.map((component, index) => (
            <Box dir='auto' key={index}>
              <Typography color={palette.colors.black} variant='font16Bold'>
                {component.label} {component.value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    default:
      return null;
  }
};

interface CarouselControlProps {
  emblaApi: EmblaApi;
  text: DynamicCardStepComponent['component_fields']['forethought_text'];
}

const CarouselControl = ({ emblaApi, text }: CarouselControlProps) => {
  const {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
  } = usePrevNextButtons(emblaApi);

  return (
    <Box
      alignItems='center'
      display='flex'
      justifyContent='space-between'
      paddingBottom='16px'
    >
      <OrgThemedButton
        disabled={prevBtnDisabled}
        onClick={onPrevButtonClick}
        size='medium'
        startIcon={<IconChevronLeft size={20} />}
        sx={ORG_THEMED_TEXT_BUTTON_SX}
        // Disable keyboard navigation for `previous` and `next` buttons, since
        // the cards themselves have keyboard navigation:
        tabIndex={-1}
        variant='text'
      >
        {text.previous}
      </OrgThemedButton>
      <OrgThemedButton
        disabled={nextBtnDisabled}
        endIcon={<IconChevronRight size={20} />}
        onClick={onNextButtonClick}
        size='medium'
        sx={ORG_THEMED_TEXT_BUTTON_SX}
        // Disable keyboard navigation for `previous` and `next` buttons, since
        // the cards themselves have keyboard navigation:
        tabIndex={-1}
        variant='text'
      >
        {text.next}
      </OrgThemedButton>
    </Box>
  );
};

const EmptyImage = () => {
  const { palette } = useTheme();

  return (
    <Box
      alignItems='center'
      bgcolor={palette.colors.slate[200]}
      borderRadius='8px'
      display='flex'
      height={IMAGE_HEIGHT}
      justifyContent='center'
      width='100%'
    >
      <Box alignItems='center' display='flex' flexDirection='column' gap={1.5}>
        <IconPhotoCancel color={palette.colors.grey[600]} size={24} />
        <Typography color={palette.colors.grey[600]} variant='font14'>
          No image available
        </Typography>
      </Box>
    </Box>
  );
};
